import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Switch,
  TextField,
} from "@mui/material";
import {
  BuildOutlined,
  EventRepeatOutlined,
  InsightsOutlined,
  SearchOutlined,
  AssignmentOutlined,
  DescriptionOutlined,
  PeopleOutlined,
  LinkOutlined,
  ScheduleOutlined,
  CheckCircleOutline,
} from "@mui/icons-material";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  createWorkOrder,
  updateWorkOrder,
  fetchWorkOrder,
  fetchWorkOrders,
  clearCurrentWorkOrder,
  previewRecurrence,
  regenerateFutureInstances,
  fetchAssignableUsers,
} from "../store/workOrdersSlice";
import {
  WorkOrderTypeCode,
  WorkOrderPriorityCode,
  WorkOrder,
  CreateWorkOrder as CreateWOModel,
  UpdateWorkOrder as UpdateWOModel,
  Location,
} from "../models/models";
import accountContext from "../../../account/accountContext";
import { Roles } from "../../../account/models/AccountModels";
import RecurrenceRulePicker from "./workBoard/RecurrenceRulePicker";
import ScheduleTimePicker from "./workBoard/ScheduleTimePicker";
import TeamPicker from "./teams/TeamPicker";
import { getMachines } from "../../machinery/customerMachinerySlice";
import { getUsers } from "../../settings/customerUsersSlice";
import { Machine, CustomerMachineryState } from "../../machinery/models/models";
import { User, UserState } from "../../settings/models/userModels";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import { useBreakpoint } from "../../../shared/hooks/useBreakpoint";
import useTranslation from "../../../shared/hooks/useTranslation";
import { priorityColors } from "./WorkOrderPriorityBadge";
import { RootState } from "../../../core/store";
import api from "../../../core/api";
import {
  FormPageLayout,
  FormCard,
  FormSection,
  FormFieldLabel,
  FormTextField,
  FormTextarea,
  FormDatePicker,
  FormChipSelector,
  FormActionBar,
  textFieldSx,
} from "../../../shared/components/redesign/forms";
import { dt } from "../../../shared/themes/redesignTokens";
import type { ChipOption } from "../../../shared/components/redesign/forms";

// ─── Helpers: string <-> Date for FormDatePicker ──────────────
const parseDate = (s: string): Date | null => (s ? new Date(s) : null);
const formatDate = (d: Date | null): string =>
  d ? d.toISOString().substring(0, 10) : "";

// ─── Chip option configs ─────────────────────────────────────
const buildTypeChips = (translate: (k: string) => string): ChipOption<number>[] => [
  { code: WorkOrderTypeCode.Reactive, icon: <BuildOutlined />, color: "#E67E22", label: translate("types.reactive") },
  { code: WorkOrderTypeCode.PreventiveMaintenance, icon: <EventRepeatOutlined />, color: "#2980B9", label: translate("types.preventiveMaintenance") },
  { code: WorkOrderTypeCode.PredictiveMaintenance, icon: <InsightsOutlined />, color: "#8E44AD", label: translate("types.predictiveMaintenance") },
  { code: WorkOrderTypeCode.Inspection, icon: <SearchOutlined />, color: "#16A085", label: translate("types.inspection") },
  { code: WorkOrderTypeCode.GeneralTask, icon: <AssignmentOutlined />, color: "#6B7280", label: translate("types.generalTask") },
];

const buildPriorityChips = (translate: (k: string) => string): ChipOption<number>[] => [
  { code: WorkOrderPriorityCode.Critical, color: priorityColors[WorkOrderPriorityCode.Critical], label: translate("priorities.critical"), dotOnly: true },
  { code: WorkOrderPriorityCode.High, color: priorityColors[WorkOrderPriorityCode.High], label: translate("priorities.high"), dotOnly: true },
  { code: WorkOrderPriorityCode.Normal, color: priorityColors[WorkOrderPriorityCode.Normal], label: translate("priorities.normal"), dotOnly: true },
  { code: WorkOrderPriorityCode.Low, color: priorityColors[WorkOrderPriorityCode.Low], label: translate("priorities.low"), dotOnly: true },
];

const CreateWorkOrderPage = () => {
  useRedesignSetup();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAllFutureEdit = searchParams.get("allFuture") === "1";
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("operations");
  const { isPhone, isTablet, isDesktop } = useBreakpoint();
  const isEditMode = Boolean(id);
  const canManage = accountContext.user?.role !== Roles.WORKER;

  const { currentWorkOrder: wo, loading, workOrders: existingWOs, recurrencePreview, assignableUsers, assignableUsersLoading } = useAppSelector(
    (state: RootState) => state.workOrders
  );

  const { machines, loading: machinesLoading } = useAppSelector<CustomerMachineryState>(
    (state) => state.customerMachinery
  );
  const allMachines = useMemo(() => Object.values(machines).flat(), [machines]);

  const { users, loading: usersLoading } = useAppSelector<UserState>(
    (state) => state.customerUsers
  );

  // ─── Form state ────────────────────────────────────────────
  const [title, setTitle] = useState("");
  const [workInstructions, setWorkInstructions] = useState("");
  const [description, setDescription] = useState("");
  const [typeId, setTypeId] = useState<number>(WorkOrderTypeCode.Reactive);
  const [priority, setPriority] = useState<number>(WorkOrderPriorityCode.Normal);
  const [machineId, setMachineId] = useState("");
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  const [locationId, setLocationId] = useState("");
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [allLocations, setAllLocations] = useState<Location[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [assignedToUserId, setAssignedToUserId] = useState("");
  const [assignedToUserName, setAssignedToUserName] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [serviceReportId, setServiceReportId] = useState("");
  const [parentWorkOrderId, setParentWorkOrderId] = useState("");
  const [selectedParentWO, setSelectedParentWO] = useState<WorkOrder | null>(null);
  const [dueDate, setDueDate] = useState("");
  const [scheduledStartDate, setScheduledStartDate] = useState("");
  const [scheduledEndDate, setScheduledEndDate] = useState("");
  const [estimatedDuration, setEstimatedDuration] = useState("");
  const [completionNotes, setCompletionNotes] = useState("");
  const [costStr, setCostStr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceRule, setRecurrenceRule] = useState("");

  // Time picker (progressive disclosure)
  const [scheduledStartTime, setScheduledStartTime] = useState("");
  const [showTimePicker, setShowTimePicker] = useState(false);

  // Phase 3B: Team assignment
  const [teamId, setTeamId] = useState<string | undefined>(undefined);
  const [autoAssignToLeastLoaded, setAutoAssignToLeastLoaded] = useState(true);

  // Recurrence is available for Admin users on all WO types (create mode only)
  const showRecurrenceOption = canManage && !isEditMode;

  // Combined start date: full ISO datetime when time is set, date-only otherwise
  const combinedStartDate = useMemo(() => {
    if (!scheduledStartDate) return undefined;
    if (scheduledStartTime) return `${scheduledStartDate}T${scheduledStartTime}:00.000Z`;
    return scheduledStartDate;
  }, [scheduledStartDate, scheduledStartTime]);

  // Auto-calculate end date when start time + duration are both set
  const calculatedEndDate = useMemo(() => {
    if (!combinedStartDate || !estimatedDuration || !scheduledStartTime) return undefined;
    const start = new Date(combinedStartDate);
    const end = new Date(start.getTime() + parseInt(estimatedDuration, 10) * 60000);
    return end.toISOString();
  }, [combinedStartDate, estimatedDuration, scheduledStartTime]);

  // Preview recurrence when rule changes
  useEffect(() => {
    if (isRecurring && recurrenceRule) {
      const today = new Date().toISOString().substring(0, 10);
      dispatch(previewRecurrence({ recurrenceRule, startDate: today, count: 5 }));
    }
  }, [isRecurring, recurrenceRule, dispatch]);

  // ─── Memoised chip options (depend on translate) ───────────
  const typeChips = useMemo(() => buildTypeChips(translate), [translate]);
  const priorityChips = useMemo(() => buildPriorityChips(translate), [translate]);

  // ─── Load reference data (machines, users, existing WOs) ───
  useEffect(() => {
    dispatch(getMachines({}));
    dispatch(getUsers());
    dispatch(fetchWorkOrders({ pageNumber: 1, pageSize: 200 }));
  }, [dispatch]);

  // RC-5: Fetch assignable users when locationId changes
  useEffect(() => {
    dispatch(fetchAssignableUsers(locationId || undefined));
  }, [dispatch, locationId]);

  // ─── Load locations ──────────────────────────────────────────
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLocationsLoading(true);
        const config = { baseURL: (window as any).app.config.customersApiBaseUrl };
        const res = await api.get<any[]>("/v1/locations", config);
        const raw = res.data || [];
        // Flatten: API returns top-level with nested childLocations
        const flat: Location[] = [];
        raw.forEach((parent: any) => {
          flat.push({ id: parent.id, name: parent.name, parentLocationId: parent.parentLocationId, parentLocationName: parent.parentLocationName });
          (parent.childLocations || []).forEach((child: any) => {
            flat.push({ id: child.id, name: child.name, parentLocationId: child.parentLocationId || parent.id, parentLocationName: parent.name });
          });
        });
        setAllLocations(flat);
      } catch {
        setAllLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };
    fetchLocations();
  }, []);

  // ─── Load existing WO in edit mode ─────────────────────────
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchWorkOrder(id));
    }
    return () => { dispatch(clearCurrentWorkOrder()); };
  }, [isEditMode, id, dispatch]);

  useEffect(() => {
    if (isEditMode && wo) {
      setTitle(wo.title);
      setWorkInstructions(wo.workInstructions || "");
      setDescription(wo.description || "");
      setTypeId(wo.workOrderTypeId);
      setPriority(wo.priorityCode);
      setMachineId(wo.machineId || "");
      setLocationId(wo.locationId || "");
      setAssignedToUserId(wo.assignedToUserId || "");
      setAssignedToUserName(wo.assignedToUserName || "");
      setServiceReportId(wo.serviceReportId || "");
      setParentWorkOrderId(wo.parentWorkOrderId || "");
      setDueDate(wo.dueDate ? wo.dueDate.substring(0, 10) : "");
      if (wo.scheduledStartDate) {
        setScheduledStartDate(wo.scheduledStartDate.substring(0, 10));
        // Extract time if present and not midnight (indicates a timed event)
        const startDateObj = new Date(wo.scheduledStartDate);
        const hours = startDateObj.getUTCHours();
        const minutes = startDateObj.getUTCMinutes();
        if (hours !== 0 || minutes !== 0) {
          setScheduledStartTime(
            `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`
          );
          setShowTimePicker(true);
        }
      } else {
        setScheduledStartDate("");
      }
      setScheduledEndDate(wo.scheduledEndDate ? wo.scheduledEndDate.substring(0, 10) : "");
      setEstimatedDuration(wo.estimatedDurationMinutes?.toString() || "");
      setCompletionNotes(wo.completionNotes || "");
      setCostStr(wo.cost?.toString() || "");
    }
  }, [isEditMode, wo]);

  // ─── Resolve selected items from loaded data ──────────────
  useEffect(() => {
    if (isEditMode && wo && allMachines.length > 0 && wo.machineId) {
      const found = allMachines.find(m => m.id === wo.machineId);
      if (found) setSelectedMachine(found);
    }
  }, [isEditMode, wo, allMachines]);

  useEffect(() => {
    if (isEditMode && wo && allLocations.length > 0 && wo.locationId) {
      const found = allLocations.find(l => l.id === wo.locationId);
      if (found) {
        setSelectedLocation(found);
        setLocationId(wo.locationId);
      }
    }
  }, [isEditMode, wo, allLocations]);

  useEffect(() => {
    if (isEditMode && wo && users.length > 0 && wo.assignedToUserId) {
      const found = users.find(u => u.id === wo.assignedToUserId);
      if (found) setSelectedUser(found);
    }
  }, [isEditMode, wo, users]);

  useEffect(() => {
    if (isEditMode && wo && existingWOs.length > 0 && wo.parentWorkOrderId) {
      const found = existingWOs.find(w => w.id === wo.parentWorkOrderId);
      if (found) setSelectedParentWO(found);
    }
  }, [isEditMode, wo, existingWOs]);

  // ─── Submit ────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!title.trim() || submitting) return;
    setSubmitting(true);

    const base: CreateWOModel = {
      title: title.trim(),
      description: description.trim() || undefined,
      workInstructions: workInstructions.trim() || undefined,
      workOrderTypeId: typeId,
      priorityCode: priority,
      machineId: machineId || undefined,
      locationId: locationId || undefined,
      assignedToUserId: assignedToUserId || undefined,
      assignedToUserName: assignedToUserName || undefined,
      serviceReportId: serviceReportId || undefined,
      parentWorkOrderId: parentWorkOrderId || undefined,
      dueDate: dueDate || undefined,
      scheduledStartDate: combinedStartDate || undefined,
      scheduledEndDate: (scheduledEndDate || calculatedEndDate) || undefined,
      estimatedDurationMinutes: estimatedDuration ? parseInt(estimatedDuration, 10) : undefined,
      recurrenceRule: isRecurring && recurrenceRule ? recurrenceRule : undefined,
      teamId: teamId || undefined,
      autoAssignToLeastLoaded: teamId ? autoAssignToLeastLoaded : undefined,
    };

    try {
      if (isEditMode && id) {
        const updateModel: UpdateWOModel = {
          ...base,
          completionNotes: completionNotes.trim() || undefined,
          cost: costStr ? parseFloat(costStr) : undefined,
        };
        await dispatch(updateWorkOrder({ id, model: updateModel })).unwrap();
        // If editing a recurring template with "all future" flag,
        // regenerate future instances from the updated template
        if (isAllFutureEdit) {
          await dispatch(regenerateFutureInstances(id)).unwrap();
        }
        navigate(`/operations/work-orders/${id}`);
      } else {
        const created = await dispatch(createWorkOrder(base)).unwrap();
        navigate(`/operations/work-orders/${created.id}`);
      }
    } catch (err) {
      console.error("[CreateWorkOrder] submit failed:", err);
      // If create mode and WO was likely created server-side despite error,
      // navigate to work board so user isn't stuck on the form
      if (!isEditMode) {
        navigate("/operations/work-board");
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    title, description, workInstructions, typeId, priority, machineId, locationId, assignedToUserId,
    assignedToUserName, serviceReportId, parentWorkOrderId,
    dueDate, combinedStartDate, scheduledEndDate, calculatedEndDate,
    estimatedDuration, completionNotes, costStr, isEditMode, id,
    dispatch, navigate, submitting, isRecurring, recurrenceRule,
    teamId, autoAssignToLeastLoaded, isAllFutureEdit,
  ]);

  const isValid = useMemo(() => title.trim().length > 0, [title]);

  const backPath = isEditMode ? `/operations/work-orders/${id}` : "/operations/work-board";

  // ─── Duration pill configs (responsive labels) ─────────────
  const durationPills = useMemo(() => {
    if (isPhone) {
      return [
        { label: "15m", value: "15" },
        { label: "30m", value: "30" },
        { label: "1h", value: "60" },
        { label: "2h", value: "120" },
        { label: "4h", value: "240" },
        { label: "8h", value: "480" },
      ];
    }
    return [
      { label: "15 min", value: "15" },
      { label: "30 min", value: "30" },
      { label: "1h", value: "60" },
      { label: "2h", value: "120" },
      { label: "4h", value: "240" },
      { label: "8h (Shift)", value: "480" },
    ];
  }, [isPhone]);

  // Stagger animation delays for each card
  const cardDelay = (index: number) => `${0.1 + index * 0.07}s`;

  return (
    <FormPageLayout
      title={isEditMode ? translate("editWorkOrder") : translate("createWorkOrder")}
      onBack={() => navigate(backPath)}
      backLabel={translate("common:back") || "Back"}
    >
      {/* Wrapper for 5 cards with vertical gap */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: isPhone ? 2 : 2.5, pb: isPhone ? "70px" : 0 }}>

        {/* ─── Card 1: BASICS ──────────────────────────────────── */}
        <FormCard animation={`fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) ${cardDelay(0)} both`}>
          {/* "All future" mode banner */}
          {isEditMode && isAllFutureEdit && (
            <div className="flex items-center gap-2 px-4 py-3 mb-1 rounded-lg bg-[#3B82F6]/5 border border-[#3B82F6]/20">
              <EventRepeatOutlined sx={{ fontSize: 16, color: "#3B82F6" }} />
              <span className="font-sora text-[13px] font-medium text-[#3B82F6]">
                {translate("recurringEdit.allFutureBanner")}
              </span>
            </div>
          )}

          <FormSection icon={<DescriptionOutlined />} label={translate("sections.basics") || "Basics"}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {/* Title */}
              <Box>
                <FormFieldLabel required>{translate("fields.title")}</FormFieldLabel>
                <FormTextField
                  value={title}
                  onChange={setTitle}
                  placeholder={translate("fields.titlePlaceholder")}
                />
              </Box>

              {/* Work Instructions */}
              <Box>
                <FormFieldLabel>{translate("fields.workInstructions")}</FormFieldLabel>
                <FormTextarea
                  value={workInstructions}
                  onChange={setWorkInstructions}
                  rows={3}
                  minHeight="80px"
                  placeholder={translate("fields.workInstructionsPlaceholder") || "What needs to be done? Steps, parts, tools..."}
                />
              </Box>

              {/* Work Order Type picker */}
              <Box>
                <FormFieldLabel>{translate("fields.type")}</FormFieldLabel>
                <FormChipSelector
                  options={typeChips}
                  value={typeId}
                  onChange={setTypeId}
                />
              </Box>

              {/* Priority picker */}
              <Box>
                <FormFieldLabel>{translate("fields.priority")}</FormFieldLabel>
                <FormChipSelector
                  options={priorityChips}
                  value={priority}
                  onChange={setPriority}
                />
              </Box>

              {/* Additional Context (renamed Description) */}
              <Box>
                <FormFieldLabel>{translate("fields.additionalContext")}</FormFieldLabel>
                <FormTextarea
                  value={description}
                  onChange={setDescription}
                  rows={3}
                  minHeight="80px"
                  placeholder={translate("fields.additionalContextPlaceholder") || "Problem description or notes (auto-filled when linked to SR)"}
                />
              </Box>
            </Box>
          </FormSection>
        </FormCard>

        {/* ─── Card 2: ASSIGNMENT ──────────────────────────────── */}
        <FormCard animation={`fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) ${cardDelay(1)} both`}>
          <FormSection icon={<PeopleOutlined />} label={translate("sections.assignment") || "Assignment"}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {/* Machine (Autocomplete) */}
              <Box>
                <FormFieldLabel>{translate("fields.machine")}</FormFieldLabel>
                <Autocomplete
                  options={allMachines}
                  value={selectedMachine}
                  loading={machinesLoading}
                  getOptionLabel={(m) => {
                    const brand = m.customBrandName || m.manufacturer?.name || "";
                    return brand ? `${brand} ${m.model}` : m.model;
                  }}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  onChange={(_, val) => {
                    setSelectedMachine(val);
                    setMachineId(val?.id || "");
                    // Auto-inherit location from machine if WO has no location yet
                    if (val?.locationId && !locationId) {
                      const loc = allLocations.find(l => l.id === val.locationId);
                      if (loc) {
                        setSelectedLocation(loc);
                        setLocationId(loc.id);
                      }
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={translate("fields.selectMachine")}
                      sx={textFieldSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {machinesLoading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              {/* Location */}
              <Box>
                <FormFieldLabel>{translate("fields.location")}</FormFieldLabel>
                <Autocomplete
                  options={allLocations}
                  value={selectedLocation}
                  loading={locationsLoading}
                  getOptionLabel={(loc) =>
                    loc.parentLocationName
                      ? `${loc.parentLocationName} > ${loc.name}`
                      : loc.name
                  }
                  groupBy={(loc) => loc.parentLocationName || loc.name}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  onChange={(_, val) => {
                    setSelectedLocation(val);
                    setLocationId(val?.id || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={translate("fields.selectLocation")}
                      sx={textFieldSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {locationsLoading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              {/* Assign To — RC-5: use location-filtered assignable users when location is set */}
              <Box>
                <FormFieldLabel>{translate("fields.assignedTo")}</FormFieldLabel>
                {locationId && assignableUsers.length > 0 ? (
                  <Autocomplete
                    options={assignableUsers}
                    value={assignableUsers.find(u => u.userId === assignedToUserId) || null}
                    loading={assignableUsersLoading}
                    getOptionLabel={(u) => u.userName}
                    isOptionEqualToValue={(opt, val) => opt.userId === val.userId}
                    onChange={(_, val) => {
                      if (val) {
                        setAssignedToUserId(val.userId);
                        setAssignedToUserName(val.userName);
                        const fullUser = users.find(u => u.id === val.userId);
                        setSelectedUser(fullUser || null);
                      } else {
                        setAssignedToUserId("");
                        setAssignedToUserName("");
                        setSelectedUser(null);
                      }
                    }}
                    renderOption={(props, option) => {
                      const { key, ...rest } = props as any;
                      return (
                        <li key={key} {...rest}>
                          {option.userName} {option.teamName ? `(${option.teamName})` : ""} - {option.openWorkOrderCount} open
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={translate("fields.selectUser")}
                        sx={textFieldSx}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {assignableUsersLoading ? <CircularProgress size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                ) : (
                  <Autocomplete
                    options={users}
                    value={selectedUser}
                    loading={usersLoading}
                    getOptionLabel={(u) => `${u.firstName} ${u.lastName}`}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    onChange={(_, val) => {
                      setSelectedUser(val);
                      setAssignedToUserId(val?.id || "");
                      setAssignedToUserName(val ? `${val.firstName} ${val.lastName}` : "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={translate("fields.selectUser")}
                        sx={textFieldSx}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {usersLoading ? <CircularProgress size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )}
              </Box>

              {/* Phase 3B: Team picker */}
              <TeamPicker
                value={teamId}
                onChange={(v) => {
                  setTeamId(v);
                  // Do NOT clear manually-assigned person when a team is selected.
                  // Auto-assign is a server-side fallback — only used when no person is set.
                }}
                autoAssign={autoAssignToLeastLoaded}
                onAutoAssignChange={(v) => {
                  setAutoAssignToLeastLoaded(v);
                  // Do NOT clear manually-assigned person when auto-assign toggles.
                }}
                locationId={locationId || undefined}
                locationName={selectedLocation?.name}
              />
            </Box>
          </FormSection>
        </FormCard>

        {/* ─── Card 3: RELATIONSHIPS ───────────────────────────── */}
        <FormCard animation={`fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) ${cardDelay(2)} both`}>
          <FormSection icon={<LinkOutlined />} label={translate("sections.relationships") || "Relationships"}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {/* Link to Service Report */}
              <Box>
                <FormFieldLabel>{translate("fields.serviceReport")}</FormFieldLabel>
                <FormTextField
                  value={serviceReportId}
                  onChange={setServiceReportId}
                  placeholder="e.g. SR-00123"
                />
              </Box>

              {/* Link to Parent Work Order (Autocomplete) */}
              <Box>
                <FormFieldLabel>{translate("fields.parentWorkOrder")}</FormFieldLabel>
                <Autocomplete
                  options={existingWOs.filter(w => !id || w.id !== id)}
                  value={selectedParentWO}
                  getOptionLabel={(w) => `${w.number} — ${w.title}`}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  onChange={(_, val) => {
                    setSelectedParentWO(val);
                    setParentWorkOrderId(val?.id || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={translate("fields.selectParentWorkOrder")}
                      sx={textFieldSx}
                    />
                  )}
                />
              </Box>
            </Box>
          </FormSection>
        </FormCard>

        {/* ─── Card 4: SCHEDULE ────────────────────────────────── */}
        <FormCard animation={`fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) ${cardDelay(3)} both`}>
          <FormSection icon={<ScheduleOutlined />} label={translate("sections.schedule") || "Schedule"}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {/* Due date */}
              <Box>
                <FormFieldLabel>{translate("fields.dueDate")}</FormFieldLabel>
                <FormDatePicker
                  value={parseDate(dueDate)}
                  onChange={(d) => setDueDate(formatDate(d))}
                />
              </Box>

              {/* Scheduled start/end — side-by-side on tablet/desktop, stacked on phone */}
              <Box sx={{ display: "flex", gap: 2, flexDirection: isPhone ? "column" : "row" }}>
                <Box sx={{ flex: 1 }}>
                  <FormFieldLabel>{translate("fields.scheduledStart")}</FormFieldLabel>
                  <FormDatePicker
                    value={parseDate(scheduledStartDate)}
                    onChange={(d) => {
                      setScheduledStartDate(formatDate(d));
                      if (!d) {
                        setScheduledStartTime("");
                        setShowTimePicker(false);
                      }
                    }}
                  />
                  <ScheduleTimePicker
                    dateValue={scheduledStartDate}
                    timeValue={scheduledStartTime}
                    onTimeChange={setScheduledStartTime}
                    showPicker={showTimePicker}
                    onTogglePicker={setShowTimePicker}
                    estimatedDurationMinutes={estimatedDuration ? parseInt(estimatedDuration, 10) : undefined}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormFieldLabel>{translate("fields.scheduledEnd")}</FormFieldLabel>
                  <FormDatePicker
                    value={parseDate(scheduledEndDate)}
                    onChange={(d) => setScheduledEndDate(formatDate(d))}
                  />
                </Box>
              </Box>

              {/* Estimated duration */}
              <Box>
                <FormFieldLabel>{translate("fields.estimatedDuration")} (min)</FormFieldLabel>
                <FormTextField
                  type="number"
                  value={estimatedDuration}
                  onChange={setEstimatedDuration}
                  inputProps={{ min: 0 }}
                />
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                  {durationPills.map((pill) => {
                    const isActive = estimatedDuration === pill.value;
                    return (
                      <Box
                        key={pill.value}
                        onClick={() => setEstimatedDuration(pill.value)}
                        sx={{
                          fontFamily: dt.font,
                          fontSize: "12px",
                          fontWeight: 600,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: dt.radiusPill,
                          border: `1px solid ${isActive ? dt.blue : dt.cardBorder}`,
                          backgroundColor: isActive ? dt.blueSoft : "transparent",
                          color: isActive ? dt.blue : dt.textSecondary,
                          cursor: "pointer",
                          userSelect: "none",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            borderColor: dt.blue,
                            color: dt.blue,
                            backgroundColor: dt.blueSoft,
                          },
                        }}
                      >
                        {pill.label}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Recurrence section (Admin only, all WO types, create mode only) */}
              {showRecurrenceOption && (
                <Box sx={{ borderTop: `1px solid ${dt.cardBorder}`, pt: 2 }}>
                  <Box sx={{
                    borderRadius: "12px",
                    backgroundColor: isRecurring ? "rgba(212, 87, 43, 0.04)" : dt.inputBg,
                    border: isRecurring ? "1px solid rgba(212, 87, 43, 0.15)" : `1px solid ${dt.cardBorder}`,
                    padding: isRecurring ? "14px 16px" : "10px 16px",
                    transition: "all 0.2s ease",
                  }}>
                    {/* Toggle row: icon + label LEFT, switch RIGHT */}
                    <Box
                      onClick={() => setIsRecurring(!isRecurring)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontFamily: dt.font,
                        fontSize: "13px",
                        fontWeight: 600,
                        color: isRecurring ? "#D4572B" : dt.textPrimary,
                      }}>
                        <EventRepeatOutlined sx={{ fontSize: 18, color: isRecurring ? "#D4572B" : dt.textSecondary }} />
                        {translate("recurrence.makeRecurring")}
                      </Box>
                      <Switch
                        checked={isRecurring}
                        onChange={(_, checked) => setIsRecurring(checked)}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: "#D4572B" },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#D4572B" },
                        }}
                      />
                    </Box>
                    {isRecurring && (
                      <Box sx={{ mt: 2 }}>
                        <RecurrenceRulePicker
                          value={recurrenceRule}
                          onChange={setRecurrenceRule}
                          startDate={dueDate || new Date().toISOString().substring(0, 10)}
                          previewDates={recurrencePreview}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </FormSection>
        </FormCard>

        {/* ─── Card 5: COMPLETION (edit-only) ──────────────────── */}
        {isEditMode && (
          <FormCard animation={`fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) ${cardDelay(4)} both`}>
            <FormSection icon={<CheckCircleOutline />} label={translate("sections.completion") || "Completion"}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: -1 }}>
                  <Box sx={{
                    fontFamily: dt.font,
                    fontSize: "11px",
                    fontWeight: 600,
                    color: dt.textMuted,
                    backgroundColor: "rgba(249, 115, 22, 0.08)",
                    border: "1px solid rgba(249, 115, 22, 0.15)",
                    borderRadius: dt.radiusPill,
                    px: 1.5,
                    py: 0.25,
                  }}>
                    {translate("sections.editOnly") || "Edit only"}
                  </Box>
                </Box>
                <Box>
                  <FormFieldLabel>{translate("fields.completionNotes")}</FormFieldLabel>
                  <FormTextarea
                    value={completionNotes}
                    onChange={setCompletionNotes}
                    rows={2}
                    minHeight="60px"
                  />
                </Box>
                <Box>
                  <FormFieldLabel>{translate("fields.cost")}</FormFieldLabel>
                  <FormTextField
                    type="number"
                    value={costStr}
                    onChange={setCostStr}
                  />
                </Box>
              </Box>
            </FormSection>
          </FormCard>
        )}

        {/* ─── Action Bar ──────────────────────────────────────── */}
        {isPhone ? (
          // Phone: sticky footer bar
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 50,
              backgroundColor: dt.cardBg,
              borderTop: `1px solid ${dt.cardBorder}`,
              boxShadow: "0 -2px 12px rgba(0,0,0,0.06)",
              px: 2,
              py: 1.5,
              animation: "fadeUp 0.3s cubic-bezier(0.4,0,0.2,1) 0.2s both",
            }}
          >
            <FormActionBar
              onCancel={() => navigate(backPath)}
              onSubmit={handleSubmit}
              submitLabel={isEditMode ? translate("editWorkOrder") : translate("createWorkOrder")}
              submitDisabled={!isValid || loading || submitting}
              loading={submitting}
              animation={false}
            />
          </Box>
        ) : (
          // Tablet/Desktop: inline action bar
          <FormActionBar
            onCancel={() => navigate(backPath)}
            onSubmit={handleSubmit}
            submitLabel={isEditMode ? translate("editWorkOrder") : translate("createWorkOrder")}
            submitDisabled={!isValid || loading || submitting}
            loading={submitting}
          />
        )}
      </Box>
    </FormPageLayout>
  );
};

export default CreateWorkOrderPage;
